import { supabase } from "@/integrations/supabase/client";
import { PodcastSource, PodcastEpisode } from "@/types/podcast";

export const getPodcasts = async (language = 'en'): Promise<PodcastSource[]> => {
  let query = supabase
    .from('podcast_sources')
    .select(`
      *,
      podcast_episodes!podcast_episodes_podcast_id_fkey(count)
    `)
    .eq('is_active', true)
    .order('name');

  // Each edition only gets the shows recorded in its own language.
  query = query.eq('language', language);

  const { data, error } = await query;

  if (error) throw error;

  return (data || []).map(podcast => ({
    ...podcast,
    episode_count: podcast.podcast_episodes?.[0]?.count || 0
  }));
};

export const getPodcastById = async (id: string): Promise<PodcastSource | null> => {
  const { data, error } = await supabase
    .from('podcast_sources')
    .select(`
      *,
      podcast_episodes!podcast_episodes_podcast_id_fkey(count)
    `)
    .eq('id', id)
    .single();

  if (error) throw error;

  return data ? {
    ...data,
    episode_count: data.podcast_episodes?.[0]?.count || 0
  } : null;
};

export const getEpisodes = async (
  podcastId?: string,
  page: number = 0,
  limit: number = 20
): Promise<{ episodes: PodcastEpisode[]; hasMore: boolean }> => {
  let query = supabase
    .from('podcast_episodes')
    .select(`
      *,
      podcast:podcast_sources!podcast_episodes_podcast_id_fkey(*)
    `)
    .eq('is_published', true)
    .order('published_at', { ascending: false })
    .range(page * limit, (page + 1) * limit);

  if (podcastId) {
    query = query.eq('podcast_id', podcastId);
  }

  const { data, error } = await query;

  if (error) throw error;

  return {
    episodes: data || [],
    hasMore: (data || []).length === limit + 1
  };
};

export const getEpisodeById = async (id: string): Promise<PodcastEpisode | null> => {
  const { data, error } = await supabase
    .from('podcast_episodes')
    .select(`
      *,
      podcast:podcast_sources!podcast_episodes_podcast_id_fkey(*)
    `)
    .eq('id', id)
    .single();

  if (error) throw error;
  return data;
};

export const toggleEpisodeLike = async (episodeId: string, userId: string): Promise<boolean> => {
  const { data: existingLike } = await supabase
    .from('podcast_episode_likes')
    .select('id')
    .eq('episode_id', episodeId)
    .eq('user_id', userId)
    .maybeSingle();

  if (existingLike) {
    const { error } = await supabase
      .from('podcast_episode_likes')
      .delete()
      .eq('id', existingLike.id);
    
    if (error) throw error;
    return false;
  } else {
    const { error } = await supabase
      .from('podcast_episode_likes')
      .insert({ episode_id: episodeId, user_id: userId });
    
    if (error) throw error;
    return true;
  }
};

export const isEpisodeLiked = async (episodeId: string, userId: string): Promise<boolean> => {
  const { data } = await supabase
    .from('podcast_episode_likes')
    .select('id')
    .eq('episode_id', episodeId)
    .eq('user_id', userId)
    .maybeSingle();

  return !!data;
};

export const getEpisodesForWeek = async (
  weekStart: string,
  weekEnd: string,
  limit: number = 6
): Promise<PodcastEpisode[]> => {
  const { data, error } = await supabase
    .from('podcast_episodes')
    .select(`
      *,
      podcast:podcast_sources!podcast_episodes_podcast_id_fkey(*)
    `)
    .eq('is_published', true)
    .gte('published_at', `${weekStart}T00:00:00Z`)
    .lte('published_at', `${weekEnd}T23:59:59Z`)
    .order('published_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return (data || []) as PodcastEpisode[];
};
